import { Tag } from '@ardoq/status-ui';
import { AppliedSettingsRow } from './AppliedSettingsRow';
import {
  ComponentRepresentation,
  ComponentRepresentationProps,
} from '@ardoq/basic-components';
import { IconName, IconSize } from '@ardoq/icons';
import { TruncatedTextPopover } from '@ardoq/popovers';
import { FlexBox } from '@ardoq/layout';
import { ContextComponentTypeSettings } from './types';
import styled from 'styled-components';
import { text2 } from '@ardoq/typography';
import { s4 } from '@ardoq/design-tokens';
import { RepresentationData } from '@ardoq/data-model';
import { tagStyles } from './atoms';
import { t, type I18nLocale } from '@ardoq/i18n';

const StyledComponentRepresentation = styled(ComponentRepresentation)`
  margin-left: 0;
`;

const StyledSpace = styled(FlexBox)`
  max-width: 120px;
  height: 18px;
  align-items: center;
  ${text2};
  ${tagStyles};
  padding: 0;
`;

const IconPrefix = styled.span`
  display: inline-flex;
  vertical-align: middle;
  margin-right: ${s4};
`;

type ComponentTagProps = {
  contextComponentType: string;
  representationData: RepresentationData | ComponentRepresentationProps;
};

const ComponentTag = ({
  contextComponentType,
  representationData,
}: ComponentTagProps) => {
  const popoverContent = (
    <FlexBox gap="small" align="center" style={{ maxWidth: '270px' }}>
      {representationData.icon && (
        <StyledComponentRepresentation
          iconSize={IconSize.SMALL}
          {...representationData}
        />
      )}
      {contextComponentType}
    </FlexBox>
  );

  return (
    <Tag>
      <StyledSpace>
        <TruncatedTextPopover
          prefix={
            representationData.icon && (
              <IconPrefix>
                <StyledComponentRepresentation
                  iconSize={IconSize.SMALL}
                  {...representationData}
                  // Ignore icon color
                  style={undefined}
                />
              </IconPrefix>
            )
          }
          content={() => popoverContent}
        >
          {contextComponentType}
        </TruncatedTextPopover>
      </StyledSpace>
    </Tag>
  );
};

type Props = {
  settings: ContextComponentTypeSettings;
  i18nLocale: I18nLocale;
};
export const ContextComponentType = ({ settings, i18nLocale }: Props) => {
  const { contextComponentType, representationData } = settings;

  if (!contextComponentType || !representationData) {
    return null;
  }

  const tag = (
    <ComponentTag
      key="ContextComponentType"
      contextComponentType={contextComponentType}
      representationData={representationData}
    />
  );

  return (
    <AppliedSettingsRow
      label={t(
        i18nLocale,
        'appLayout:contextComponentTypeLabel',
        'Context component type'
      )}
      tags={[tag]}
      icon={IconName.API}
      isDisabled={!contextComponentType}
      i18nLocale={i18nLocale}
    />
  );
};
